import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import Moment from "react-moment";
import ContentContainer from "../../templates/content-container/contentContainer";
import ContentContainerToolbar from "../../ui-components/molecules/content-container-toolbar/contentContainerToolbar";
import Table from "../../ui-components/table/table";
import { createColumnHelper } from "@tanstack/react-table";
import Amount from "../../ui-components/molecules/amount/amount";
import Button, { ButtonThemes } from "../../ui-components/button/button";
import XGSErrorMessage from "../../ui-components/error-message/errorMessage";
import { userSelector } from "../../slices/user/userSlice";
import PaymentService from "../../app/data/payment/paymentService";
import { PaymentSummary } from "../../app/data/payment/PaymentSession";
import { Routes } from "../../app/route/RoutesConfig";
import { goToPaymentStatus } from "../../app/data/payment/paymentUtils";
import { TagColor } from "../../app/data/common/tagColor";
import Tag from "../../ui-components/molecules/tag/tag";
import "./paymentsList.scss";

const paymentService = PaymentService.getInstance();
const columnHelper = createColumnHelper<PaymentSummary>();

const statusColor = (status: string): TagColor => {
  switch (status) {
    case "COMPLETED":
      return TagColor.GREEN;
    case "PENDING":
    case "CREATED":
      return TagColor.BLUE;
    case "FAILED":
      return TagColor.RED;
    default:
      return TagColor.GREY;
  }
};

const PaymentsList: React.FC = () => {
  const userState = useSelector(userSelector);
  const history = useHistory();
  const accountNumber = userState.activeSubAccount?.accountNumber;
  const [rows, setRows] = useState<PaymentSummary[]>([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!accountNumber) return;
    setLoading(true);
    setError(null);
    const res = await paymentService.listPayments({
      accountNumber,
      status: status || undefined,
      page,
      size: 25,
    });
    if (res.ok() && res.data) {
      setRows(res.data.content || []);
      setTotalPages(res.data.totalPages || 0);
    } else {
      setError(typeof res.getError?.() === "string" ? (res.getError() as string) : "Failed to load payments");
      setRows([]);
    }
    setLoading(false);
  }, [accountNumber, page, status]);

  useEffect(() => {
    load();
  }, [load]);

  const columns = useMemo(
    () => [
      columnHelper.accessor("dateCreated", {
        header: "Date",
        cell: (info) =>
          info.getValue() ? <Moment format="MM/DD/YYYY h:mm A">{info.getValue()}</Moment> : "—",
      }),
      columnHelper.accessor("status", {
        header: "Status",
        cell: (info) => (
          <Tag mods={{ color: statusColor(info.getValue()) }}>{info.getValue()}</Tag>
        ),
      }),
      columnHelper.accessor("amountCents", {
        header: "Amount",
        cell: (info) =>
          info.getValue() != null ? <Amount amount={(info.getValue() as number) / 100} /> : "—",
      }),
      columnHelper.accessor("paymentMethod", {
        header: "Method",
        cell: (info) => {
          const row = info.row.original;
          if (row.paymentMethodLabel) return row.paymentMethodLabel;
          const method = info.getValue();
          if (method === "CC") return "Credit Card";
          if (method === "CK" || method === "BANK") return "eCheck";
          return method || "—";
        },
      }),
      columnHelper.accessor("invoiceNumbers", {
        header: "Invoices",
        cell: (info) => (info.getValue() || []).join(", ") || "—",
      }),
      columnHelper.display({
        id: "actions",
        header: "",
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="xgs-payments-list__actions">
              <Button
                theme={ButtonThemes.blue}
                onClick={(e) => {
                  e.stopPropagation();
                  goToPaymentStatus(history, row.paymentId);
                }}
              >
                View status
              </Button>
              {row.status === "COMPLETED" && (
                <Button
                  theme={ButtonThemes.gray}
                  onClick={(e) => {
                    e.stopPropagation();
                    const prefix = accountNumber ? `/${accountNumber}` : "";
                    history.push(`${prefix}${Routes.paymentReceipt}/${row.paymentId}`);
                  }}
                >
                  Receipt
                </Button>
              )}
            </div>
          );
        },
      }),
    ],
    [accountNumber, history]
  );

  return (
    <ContentContainer
      title="Payment history"
      isLoading={loading}
    >
      <ContentContainerToolbar>
        <div className="xgs-payments-list__filters">
          <label>
            Status{" "}
            <select
              value={status}
              onChange={(e) => {
                setPage(0);
                setStatus(e.target.value);
              }}
            >
              <option value="">All</option>
              <option value="COMPLETED">Completed</option>
              <option value="PENDING">Pending</option>
              <option value="FAILED">Failed</option>
              <option value="CANCELLED">Cancelled</option>
              <option value="EXPIRED">Expired</option>
            </select>
          </label>
        </div>
      </ContentContainerToolbar>
      {error && <XGSErrorMessage>{error}</XGSErrorMessage>}
      <Table columns={columns} data={rows} />
      <div className="xgs-payments-list__pager">
        <Button theme={ButtonThemes.gray} disabled={page <= 0} onClick={() => setPage((p) => p - 1)}>
          Previous
        </Button>
        <span>
          Page {page + 1} of {Math.max(totalPages, 1)}
        </span>
        <Button
          theme={ButtonThemes.gray}
          disabled={page + 1 >= totalPages}
          onClick={() => setPage((p) => p + 1)}
        >
          Next
        </Button>
      </div>
    </ContentContainer>
  );
};

export default PaymentsList;
